import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, Trash2, X } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import { ConfirmDialog } from '@/components/feedback/ConfirmDialog'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { RecordConflictAlert } from '@/components/feedback/RecordConflictAlert'
import { useToastStack } from '@/components/feedback/toast-stack'
import { EditableGrid } from '@/components/grid'
import { Button } from '@/components/ui/button'
import { useCashFirm, useInvalidateCash } from '@/features/cash/hooks'
import {
  blankGridRow,
  bulkRow,
  gridRowFromItem,
  isBlankRow,
  isRowDirty,
  manualGridColumns,
  manualGridSchema,
  MANUAL_GRID_FIELDS,
  type ManualGridRow,
} from '@/features/cash/manual-item-grid'
import { ManualItemSheet } from '@/features/cash/ManualItemSheet'
import { CashFirmSelect, CashStrip } from '@/features/cash/shared'
import { HrPageHeader as PageHeader } from '@/features/hr/HrPageHeader'
import { recordChangedError, recordChangedMessage } from '@/lib/form/record-conflict'
import {
  countRowIssues,
  ROW_FIELD,
  rowErrorsProblem,
  rowIssuesFromProblem,
  rowIssuesFromSchema,
  type RowIssues,
} from '@/lib/form/row-errors'
import { useLeaveGuard } from '@/lib/form/use-leave-guard'
import { messages } from '@/lib/messages'
import { toProblem, type Problem } from '@/lib/problem'
import { queryKeys } from '@/lib/query-keys'
import { cashApi, type CashManualItem, type CashSettings } from '@/services/http/cash'

const t = messages.cash.manual

/**
 * Sunucudaki listeyi taslağa katar: kirli ve yeni satırlar korunur, kirli olmayan kayıtlı satırlar
 * güncel hâliyle değişir (silinen düşer), taslakta olmayan yeni kalemler yeni satırlardan önce eklenir.
 */
function mergeRows(
  rows: readonly ManualGridRow[],
  items: readonly CashManualItem[],
  originals: ReadonlyMap<string, ManualGridRow>,
): ManualGridRow[] {
  const fresh = new Map(items.map((item) => [item.id, item]))
  const kept: ManualGridRow[] = []
  for (const row of rows) {
    if (row.id === null) continue
    const item = fresh.get(row.id)
    if (isRowDirty(row, originals.get(row.id))) kept.push(row)
    else if (item) kept.push(gridRowFromItem(item))
  }
  const present = new Set(kept.map((row) => row.id))
  const added = items.filter((item) => !present.has(item.id)).map(gridRowFromItem)
  return [...kept, ...added, ...rows.filter((row) => row.id === null)]
}

function originalsOf(items: readonly CashManualItem[]): Map<string, ManualGridRow> {
  return new Map(items.map((item) => [item.id, gridRowFromItem(item)]))
}

/** Satırın tamamına ait hata (`*`) açıklama hücresinde gösterilir. */
function placeRowIssues(issues: RowIssues): RowIssues {
  return Object.fromEntries(
    Object.entries(issues).map(([key, fields]) => {
      const { [ROW_FIELD]: whole, ...rest } = fields
      return [key, whole && !rest.description ? { ...rest, description: whole } : rest]
    }),
  )
}

function ManualItemsGrid({
  firm,
  items,
  settings,
  start,
  canPlan,
  header,
}: {
  firm: string
  items: CashManualItem[]
  settings: CashSettings
  start: string
  canPlan: boolean
  /** Sayfa başlığı ve şerit; firma seçici kirli ızgarada önce onay sorar (`guard`). */
  header: (guard: (action: () => void) => void) => ReactNode
}) {
  const defaultType = settings.types[0]?.name ?? ''
  const defaultPeriod = `${start.slice(0, 7)}-01`
  const [source, setSource] = useState(items)
  const [originals, setOriginals] = useState(() => originalsOf(items))
  const [rows, setRows] = useState<ManualGridRow[]>(() => [
    ...items.map(gridRowFromItem),
    ...(canPlan ? [blankGridRow(defaultType, defaultPeriod)] : []),
  ])
  const [historyKey, setHistoryKey] = useState(0)
  // Başarısız kayıt denemesinden sonra istemci doğrulaması canlı yenilenir.
  const [attempted, setAttempted] = useState(false)
  // Sunucu satır hataları, gönderilen satır nesnesiyle; satır değişince hatası düşer.
  const [serverIssues, setServerIssues] = useState<{ issues: RowIssues; sent: ManualGridRow[] }>({
    issues: {},
    sent: [],
  })
  const [conflict, setConflict] = useState<Problem | null>(null)
  const [editing, setEditing] = useState<CashManualItem | null>(null)
  const [deleting, setDeleting] = useState<CashManualItem | null>(null)
  const { toast } = useToastStack()
  const invalidate = useInvalidateCash()
  const queryClient = useQueryClient()

  // Arka plan tazelemesi (ayrıntı editörü, silme, başka sekme) taslağa katılır.
  if (items !== source) {
    setSource(items)
    setOriginals(originalsOf(items))
    setRows(mergeRows(rows, items, originals))
  }

  const dirtyRows = useMemo(
    () => rows.filter((row) => isRowDirty(row, row.id ? originals.get(row.id) : undefined)),
    [rows, originals],
  )
  const dirty = dirtyRows.length > 0
  const itemsById = useMemo(() => new Map(items.map((item) => [item.id, item])), [items])
  const columns = useMemo(
    () => manualGridColumns(settings, start, itemsById),
    [settings, start, itemsById],
  )

  const validate = (list: ManualGridRow[]) =>
    rowIssuesFromSchema(list, manualGridSchema, (row) => row.key, MANUAL_GRID_FIELDS)
  const issues = useMemo(() => {
    const live = new Set(rows)
    const server = Object.fromEntries(
      Object.entries(serverIssues.issues).filter(([key]) =>
        serverIssues.sent.some((row) => row.key === key && live.has(row)),
      ),
    )
    const client = attempted ? validate(dirtyRows) : {}
    return placeRowIssues({ ...server, ...client })
  }, [rows, dirtyRows, attempted, serverIssues])

  const discard = () => {
    setRows(
      mergeRows([], items, originals).concat(
        canPlan ? [blankGridRow(defaultType, defaultPeriod)] : [],
      ),
    )
    setAttempted(false)
    setServerIssues({ issues: {}, sent: [] })
    setConflict(null)
    setHistoryKey((k) => k + 1)
  }
  const guard = useLeaveGuard(dirty, discard)

  const reloadList = async () => {
    const list = await queryClient.fetchQuery({
      queryKey: queryKeys.cash.manual(firm),
      queryFn: () => cashApi.manualItems(firm),
      staleTime: 0,
    })
    return list
  }

  const save = useMutation({
    mutationFn: (sent: ManualGridRow[]) =>
      cashApi.bulkManual(
        firm,
        sent.map((row) => bulkRow(firm, row)),
      ),
    onSuccess: async (saved, sent) => {
      // Kaydedilen satırlar sunucu hâliyle değişir; yeni satırlar kayıtlı kalem olur.
      const byKey = new Map(sent.map((row, i) => [row.key, saved[i]!]))
      const next = rows.map((row) => {
        const item = byKey.get(row.key)
        return item ? gridRowFromItem(item) : row
      })
      setOriginals((old) => {
        const copy = new Map(old)
        for (const item of saved) copy.set(item.id, gridRowFromItem(item))
        return copy
      })
      setRows(
        next.filter((row) => !isBlankRow(row)).concat(blankGridRow(defaultType, defaultPeriod)),
      )
      setAttempted(false)
      setServerIssues({ issues: {}, sent: [] })
      setConflict(null)
      setHistoryKey((k) => k + 1)
      toast({ type: 'success', title: t.grid.saved(saved.length) })
      void invalidate()
    },
    onError: (error, sent) => {
      const rowProblem = rowErrorsProblem(error)
      if (rowProblem) {
        const found = rowIssuesFromProblem(
          rowProblem,
          sent.map((row) => row.key),
          MANUAL_GRID_FIELDS,
        )
        setServerIssues({ issues: found, sent })
        toast({ type: 'error', title: t.grid.invalid(countRowIssues(found)) })
        return
      }
      const stale = recordChangedError(error)
      if (stale) {
        setConflict(stale)
        const row = stale.row === undefined ? undefined : sent[stale.row]
        if (row) {
          setServerIssues({
            issues: { [row.key]: { [ROW_FIELD]: recordChangedMessage(stale) } },
            sent,
          })
        }
        return
      }
      toast({
        type: 'error',
        title: messages.cash.planning.failed,
        description: toProblem(error).detail ?? undefined,
      })
    },
  })

  const onSave = () => {
    const sent = dirtyRows
    if (sent.length === 0) return
    setAttempted(true)
    const found = validate(sent)
    if (countRowIssues(found) > 0) {
      toast({ type: 'error', title: t.grid.invalid(countRowIssues(found)) })
      return
    }
    save.mutate(sent)
  }

  const reload = useMutation({
    mutationFn: reloadList,
    onSuccess: (list) => {
      // Bayat satırlar güncel hâline döner; diğer düzenlemeler ve yeni satırlar korunur.
      const fresh = new Map(list.map((item) => [item.id, item]))
      setOriginals(originalsOf(list))
      setRows((old) =>
        old.flatMap((row) => {
          if (row.id === null) return [row]
          const item = fresh.get(row.id)
          if (!item) return []
          return item.version === row.version ? [row] : [gridRowFromItem(item)]
        }),
      )
      setSource(list)
      setConflict(null)
      setServerIssues({ issues: {}, sent: [] })
    },
  })

  const remove = useMutation({
    // Okunan sürümle silinir; kalem o arada değiştiyse onay kapanır, ızgaranın çakışma uyarısı açılır.
    mutationFn: (item: CashManualItem) => cashApi.deleteManual(firm, item.id, item.version),
    onSuccess: () => {
      toast({ type: 'success', title: t.deleted })
      setDeleting(null)
      void invalidate()
    },
    onError: (error) => {
      const stale = recordChangedError(error)
      if (!stale) return
      setDeleting(null)
      setConflict(stale)
    },
  })

  return (
    <>
      {header(guard.confirm)}
      {guard.dialog}
      {conflict && (
        <RecordConflictAlert
          label={recordChangedMessage(conflict)}
          onReload={() => reload.mutate()}
          pending={reload.isPending}
          failed={reload.isError}
        />
      )}
      {canPlan && (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-text-secondary mr-auto text-xs">{t.grid.hint}</p>
          <Button
            variant="secondary"
            onClick={() => setRows((old) => [...old, blankGridRow(defaultType, defaultPeriod)])}
          >
            <Plus />
            {t.grid.addRow}
          </Button>
          <Button variant="ghost" disabled={!dirty} onClick={() => guard.confirm(discard)}>
            {t.grid.discard}
          </Button>
          <Button disabled={!dirty} loading={save.isPending} onClick={onSave}>
            {t.grid.save(dirtyRows.length)}
          </Button>
        </div>
      )}
      <EditableGrid
        ariaLabel={t.grid.label}
        rows={rows}
        columns={columns}
        rowKey={(row) => row.key}
        onRowsChange={setRows}
        newRow={canPlan ? () => blankGridRow(defaultType, defaultPeriod) : undefined}
        issues={issues}
        historyKey={historyKey}
        readOnly={!canPlan}
        rowState={(row) =>
          row.id === null
            ? isBlankRow(row)
              ? undefined
              : 'new'
            : isRowDirty(row, originals.get(row.id))
              ? 'dirty'
              : undefined
        }
        rowActions={
          canPlan
            ? (row) => {
                const item = row.id ? itemsById.get(row.id) : undefined
                if (!item) {
                  return (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t.grid.removeNew}
                      onClick={() => setRows((old) => old.filter((r) => r.key !== row.key))}
                    >
                      <X />
                    </Button>
                  )
                }
                const label = `${item.type} ${item.description}`
                return (
                  <>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`${messages.cash.planning.edit} ${label}`}
                      disabled={isRowDirty(row, originals.get(item.id))}
                      onClick={() => setEditing(item)}
                    >
                      <Pencil />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`${t.delete} ${label}`}
                      onClick={() => setDeleting(item)}
                    >
                      <Trash2 />
                    </Button>
                  </>
                )
              }
            : undefined
        }
      />
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t.deleteConfirm}
        description={deleting ? `${deleting.type} · ${deleting.description}` : ''}
        confirmLabel={t.delete}
        pending={remove.isPending}
        error={
          remove.error && !recordChangedError(remove.error) ? toProblem(remove.error).detail : null
        }
        onConfirm={() => deleting && remove.mutate(deleting)}
      />
      {/* Her açılışta taslak seçilen kalemden yeniden kurulur. */}
      <ManualItemSheet
        key={editing?.id ?? 'kapali'}
        firm={firm}
        settings={settings}
        start={start}
        item={editing}
        open={editing !== null}
        onClose={() => setEditing(null)}
        onReloaded={(fresh) => setEditing(fresh)}
      />
    </>
  )
}

/** /nakit/erp-disi — ERP dışı kalemler (docs/NAKIT-PLANI-PLAN.md Faz 2, G5/G6; ızgara CRUD-ALTYAPI Faz 3). */
export function ManualItemsPage() {
  const { firms, firm, setFirm, can } = useCashFirm()
  const canPlan = can('cash.plan', 'cash.entry')

  const items = useQuery({
    queryKey: queryKeys.cash.manual(firm ?? ''),
    queryFn: () => cashApi.manualItems(firm!),
    enabled: !!firm,
  })
  const settings = useQuery({
    queryKey: queryKeys.cash.settings(firm ?? ''),
    queryFn: () => cashApi.settings(firm!),
    enabled: !!firm,
  })
  const strip = useQuery({
    queryKey: queryKeys.cash.strip(firm ?? ''),
    queryFn: () => cashApi.strip(firm!),
    enabled: !!firm,
  })

  const error = firms.error ?? items.error ?? settings.error
  const ready = items.data && settings.data && strip.data && firm
  const pageHeader = (guard: (action: () => void) => void) => (
    <PageHeader
      title={t.title}
      description={t.description}
      actions={
        <CashFirmSelect
          firms={firms.data ?? []}
          firm={firm}
          onChange={(next) => guard(() => setFirm(next))}
        />
      }
    />
  )

  if (!ready || error) {
    return (
      <div className="flex flex-col gap-4">
        {pageHeader((action) => action())}
        {error ? <ErrorState error={error} /> : <Loading rows={6} />}
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-4">
      <ManualItemsGrid
        key={firm}
        firm={firm}
        items={items.data}
        settings={settings.data}
        start={strip.data.start}
        canPlan={canPlan}
        header={(guard) => (
          <>
            {pageHeader(guard)}
            <CashStrip points={strip.data.points} />
          </>
        )}
      />
    </div>
  )
}
